import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { CreditCard, Info, Megaphone, ShieldAlert, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NotificationCategory, NotificationItem, NotificationSeverity } from '../types';

interface NotificationListItemProps {
  item: NotificationItem;
  selected?: boolean;
  onClick?: (item: NotificationItem) => void;
}

const categoryIcons: Record<NotificationCategory, React.ElementType> = {
  SYSTEM: Info,
  SECURITY: ShieldAlert,
  BILLING: CreditCard,
  PRODUCT_UPDATE: Sparkles,
  MARKETING: Megaphone,
};

const severityClass: Record<NotificationSeverity, string> = {
  INFO: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
  WARNING:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200',
  CRITICAL:
    'border-red-200 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200',
};

function formatRelativeTime(value: string, language: string) {
  const diffSeconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];
  const [unit, seconds] = units.find(([, amount]) => Math.abs(diffSeconds) >= amount) ?? [
    'second',
    1,
  ];

  return new Intl.RelativeTimeFormat(language, { numeric: 'auto' }).format(
    Math.round(diffSeconds / seconds),
    unit
  );
}

export const NotificationListItem: React.FC<NotificationListItemProps> = ({
  item,
  selected = false,
  onClick,
}) => {
  const { t, i18n } = useTranslation();
  const Icon = categoryIcons[item.category];
  const relativeTime = useMemo(
    () => formatRelativeTime(item.publishedAt, i18n.language),
    [i18n.language, item.publishedAt]
  );
  const unread = !item.readAt;

  return (
    <button
      type="button"
      onClick={() => onClick?.(item)}
      className={`flex w-full min-w-0 gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-50 dark:border-slate-800 dark:hover:bg-slate-800/70 ${
        selected ? 'bg-blue-50 dark:bg-sky-500/10' : 'bg-white dark:bg-slate-900'
      }`}
    >
      <Box
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${severityClass[item.severity]}`}
      >
        <Icon size={18} />
      </Box>
      <Box className="min-w-0 flex-1">
        <Box className="mb-1 flex min-w-0 items-start justify-between gap-2">
          <Typography
            component="span"
            className={`min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-slate-100 ${
              unread ? 'font-semibold' : 'font-medium'
            }`}
          >
            {item.title}
          </Typography>
          {unread && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />}
        </Box>
        <Typography
          component="span"
          className="mb-2 block text-xs leading-5 text-gray-600 dark:text-slate-400"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.bodyPreview}
        </Typography>
        <Box className="flex min-w-0 items-center justify-between gap-2">
          <Typography
            component="span"
            className="truncate text-xs text-gray-500 dark:text-slate-500"
          >
            {relativeTime}
          </Typography>
          {item.severity !== 'INFO' && (
            <Chip
              size="small"
              label={t(`notifications.severity.${item.severity}`)}
              className="h-5 shrink-0 text-[11px]"
              color={item.severity === 'CRITICAL' ? 'error' : 'warning'}
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    </button>
  );
};
